import type { ReactElement } from 'react';
import { MessageSquareDashed } from 'lucide-react';
import { Button, EmptyState, Skeleton } from '@cyphex/ui';
import type { ConversationId, UserId } from '@cyphex/types';
import { isApiRequestError } from '@/lib/api/errors';
import { SidePanel } from '@/components/common/SidePanel';
/* Deep import: reading the linked thread costs nothing here — the rail's own
   timeline reads the same query key — and it is how we find out whether that
   thread is readable at all. */
import { useConversation } from '@/features/chat/hooks/useConversation';
import { useCallConversation } from '../../hooks/useCallConversation';
import { CallChatThread } from './CallChatThread';

/** The rail's DOM id, so the toolbar's Chat button can own `aria-controls`. */
export const CALL_CHAT_PANEL_ID = 'call-chat-panel';

/**
 * The two answers that mean "this thread is not mine".
 *
 * 404 is the normal one: `getConversationAccess` returns nothing for a
 * conversation you hold no active membership row in, and `requireConversation`
 * turns that into `notFound('Conversation')` rather than a 403 — the API does
 * not confirm the existence of rooms you are not in. 403 is here because a
 * channel-backed conversation can be refused by the channel ACL or the guest
 * switch instead.
 */
const NOT_MINE = new Set([403, 404]);

export interface CallChatPanelProps {
  /** The call this rail belongs to. One call resolves to one thread, once. */
  callId: string;
  /** `call.conversationId` — the thread this call was placed FROM, if any. */
  linkedConversationId: ConversationId | null;
  /** Everybody else on the call, whatever state their leg is in. */
  rosterUserIds: readonly UserId[];
  /**
   * `GET /calls/:id` gave up, so `rosterUserIds` is empty because there is no
   * answer rather than because none has arrived yet. Without this the two are
   * indistinguishable here, and the rail sits on skeletons for the rest of the
   * call: the roster is bounded-retried now, but a call can outlive every
   * attempt.
   */
  rosterError: boolean;
  /** Ask for the roster again. */
  onRetryRoster: () => void;
  onClose: () => void;
}

/**
 * Chat during a call, exactly as Teams does it: the rail is the REAL thread
 * with these people, so what is said during the call is still there tomorrow.
 *
 * Mounted only while open — never hidden — because everything inside it (read
 * receipts, typing, the realtime subscription) is a signal that must not be
 * sent from a pane nobody is looking at.
 */
export function CallChatPanel(props: CallChatPanelProps) {
  /*
   * `POST /v1/calls` never authorises the conversation id it is handed, so an
   * id we turn out not to be a MEMBER of is worth exactly as much as none at
   * all: fall through and resolve the thread that belongs to this roster.
   *
   * ONLY a refusal, though. This used to fall through on `isError`, which is
   * also what a 502 and a dead network look like — and the global retry is 1
   * with no refetch on focus, so the flag latched for the life of the panel.
   * Two unlucky requests on a six-person group's own thread therefore ended
   * with the call's three-person roster hashed into a BRAND NEW group: no
   * history, and everything typed during the call left in a throwaway the
   * others would have to go looking for. A broken server is not a permission
   * decision, so it stops here instead of creating anything.
   */
  const linked = useConversation(props.linkedConversationId);
  const refused = isApiRequestError(linked.error) && NOT_MINE.has(linked.error.status);
  const unreachable = linked.isError && !refused;
  const linkedId = refused ? null : props.linkedConversationId;
  /*
   * A REFUSAL MUST NOT BE PERSISTED. The resolver writes its answer back to
   * `cyphex.calls.conversation_id` so that every party converges on one thread
   * — but only when the call genuinely has none. Here the call HAS one and we
   * merely may not read it: the conditional UPDATE could not land on it anyway,
   * and the id it would answer with is the very one we were just refused.
   */
  const resolved = useCallConversation(props.callId, linkedId, props.rosterUserIds, !refused);

  const body = (): ReactElement => {
    if (unreachable) {
      return (
        <EmptyState
          icon={MessageSquareDashed}
          tone="error"
          title="Chat is unavailable"
          description="We could not reach this call's chat. It is still in Chat, alongside your other conversations."
        />
      );
    }
    if (resolved.conversationId !== null) {
      return <CallChatThread conversationId={resolved.conversationId} />;
    }
    /*
     * THE ROSTER FAILED, NOT THE THREAD — and it is asked BEFORE `isResolving`,
     * which is true whenever the roster is empty and so cannot tell "has not
     * arrived" from "is never going to". Left unasked, the two are the same
     * skeleton, and a skeleton with nothing to press is a dead end that lasts
     * as long as the call.
     */
    if (props.rosterError) {
      return (
        <EmptyState
          icon={MessageSquareDashed}
          tone="error"
          title="Chat is unavailable"
          description="We could not work out who this call's chat is with. It is still in Chat, alongside your other conversations."
          action={
            <Button variant="primary" onClick={props.onRetryRoster}>
              Try again
            </Button>
          }
        />
      );
    }
    if (resolved.isResolving) {
      return (
        <div className="flex-1 space-y-4 p-4">
          <Skeleton shape="text" lines={3} />
          <Skeleton shape="text" lines={2} />
        </div>
      );
    }
    return (
      <EmptyState
        icon={MessageSquareDashed}
        tone="error"
        title="Chat is unavailable"
        description="We could not open the chat for this call. It is still in Chat, alongside your other conversations."
        action={
          <Button variant="primary" onClick={resolved.retry}>
            Try again
          </Button>
        }
      />
    );
  };

  return (
    <SidePanel id={CALL_CHAT_PANEL_ID} title="Chat" onClose={props.onClose}>
      {body()}
    </SidePanel>
  );
}
